import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Table, Modal, Form, Badge, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PermissionWrapper from "../components/PermissionWrapper";
import TicketManagementService from "../services/TicketManagementService";
import { apiGet, apiPost, apiDelete } from "../services/BaseService";
import { validateRequired } from "../utils/validation";
import PackageService, { GroupCredits } from "../services/PackageService";
import "../components/TicketManagement.css";

interface TicketOption {
  _id: string;
  ticketName: string;
  price: number;
  totalSeats?: number;
  availableSeats?: number;
  isActive: boolean;
  hasBookings?: boolean;
  isEditable?: boolean;
}

interface Schedule {
  _id: string;
  programId: {
    _id: string;
    title: string;
  };
  startDate: string;
  timing: string;
  address: string;
  organizerName: string;
}

const ManageTicketPage: React.FC = () => {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [ticketOptions, setTicketOptions] = useState<TicketOption[]>([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [newTicketOptions, setNewTicketOptions] = useState([{ ticketName: "", price: 0, totalSeats: 0 }]);
  const [selectedProgram, setSelectedProgram] = useState<string>("");
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [programs, setPrograms] = useState<{_id: string, title: string}[]>([]);
  const [userGroups, setUserGroups] = useState<{_id: string, name: string}[]>([]);
  const [groupCredits, setGroupCredits] = useState<GroupCredits | null>(null);


  useEffect(() => {
    fetchUserGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchPrograms();
      fetchGroupCredits();
      setSelectedProgram("");
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (selectedProgram) {
      fetchSchedules();
    } else {
      setSchedules([]);
      setSelectedSchedule(null);
    }
  }, [selectedProgram]);

  const fetchUserGroups = async () => {
    try {
      const response = await apiGet('/programs/user-groups');
      setUserGroups(response.groups || []);
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
    }
  };

  const fetchSchedules = async () => {
    if (!selectedProgram) return;
    try {
      const response = await apiGet(`/programschedules/program/${selectedProgram}`);
      setSchedules(response || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await apiGet(`/programs/group/${selectedGroup}`);
      const programs = response.programs || response;
      setPrograms(programs || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchGroupCredits = async () => {
    try {
      if (!selectedGroup) return;
      const data = await PackageService.getGroupCredits(selectedGroup);
      setGroupCredits(data);
    } catch (error) {
      console.error('Error fetching group credits:', error);
    }
  };

  const fetchTicketOptions = async (scheduleId: string) => {
    try {
      const response = await apiGet(`/tickets/schedule/${scheduleId}`);
      setTicketOptions(response);
    } catch (error) {
      console.error('Error fetching ticket options:', error);
    }
  };

  const handleScheduleSelect = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    fetchTicketOptions(schedule._id);
  };

  const handleSaveTicketOptions = async () => {
    if (!selectedSchedule) return;

    // Calculate ticket difference
    const totalNewTickets = newTicketOptions.reduce((sum, option) => sum + (option.totalSeats || 0), 0);
    const existingTickets = ticketOptions.reduce((sum, option) => sum + (option.totalSeats || 0), 0);
    const ticketDifference = totalNewTickets - existingTickets;

    // Check ticket credits if adding new tickets
    if (ticketDifference > 0 && groupCredits && (groupCredits.remainingCredits?.ticketCredits ?? 0) < ticketDifference) {
      alert(`Insufficient ticket credits! You need ${ticketDifference} credits but only have ${groupCredits.remainingCredits?.ticketCredits ?? 0} remaining. Please purchase additional credits.`);
      return;
    }

    try {
      const response = await apiPost(`/tickets/schedule/${selectedSchedule._id}`, { ticketOptions: newTicketOptions });
      
      // Adjust ticket credits based on difference
      if (ticketDifference !== 0) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        await PackageService.useCredits(selectedGroup, 0, ticketDifference);
      }
      
      // Refresh both ticket options and credits after saving
      await Promise.all([
        fetchTicketOptions(selectedSchedule._id),
        fetchGroupCredits()
      ]);
      setShowTicketModal(false);
      setNewTicketOptions([{ ticketName: "", price: 0, totalSeats: 0 }]);
    } catch (error: any) {
      if (error.response?.data?.lockedOptions) {
        alert(`Cannot modify ticket options with existing bookings: ${error.response.data.lockedOptions.join(', ')}`);
      } else {
        alert('Error saving ticket options. Please try again.');
      }
      console.error('Error saving ticket options:', error);
    }
  };

  const addTicketOption = () => {
    setNewTicketOptions([...newTicketOptions, { ticketName: "", price: 0, totalSeats: 0 }]);
  };

  const removeTicketOption = (index: number) => {
    setNewTicketOptions(newTicketOptions.filter((_, i) => i !== index));
  };

  const updateTicketOption = (index: number, field: string, value: any) => {
    const updated = [...newTicketOptions];
    updated[index] = { ...updated[index], [field]: value };
    setNewTicketOptions(updated);
  };

  return (
    <PermissionWrapper pageName="ManageTicketPage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2>
              <i className="bi bi-ticket-perforated me-2"></i>
              My Ticket Management
            </h2>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-4 g-3">
          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <Form.Label>Group</Form.Label>
              <Form.Select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)}>
                <option value="">Choose Group...</option>
                {userGroups.map(group => (
                  <option key={group._id} value={group._id}>{group.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <Form.Label>Program/Play</Form.Label>
              <Form.Select value={selectedProgram} onChange={(e) => setSelectedProgram(e.target.value)} disabled={!selectedGroup}>
                <option value="">Choose Program...</option>
                {programs.map(program => (
                  <option key={program._id} value={program._id}>{program.title}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Credits Display */}
        {selectedGroup && groupCredits && (
          <Row className="mb-3">
            <Col>
              <Alert variant={(groupCredits.remainingCredits?.ticketCredits ?? 0) > 0 ? 'success' : 'warning'} className="d-flex justify-content-between align-items-center">
                <span>
                  <i className="bi bi-ticket-perforated me-2"></i>
                  <strong>Ticket Credits:</strong> {groupCredits.remainingCredits?.ticketCredits ?? 0} remaining out of {groupCredits.totalCredits?.ticketCredits ?? 0}
                </span>
                {(groupCredits.remainingCredits?.ticketCredits ?? 0) <= 0 && (
                  <Button variant="outline-primary" size="sm" onClick={() => navigate('/dashboard/package-selection')}>
                    <i className="bi bi-plus-circle me-1"></i>Add Credits
                  </Button>
                )}
              </Alert>
            </Col>
          </Row>
        )}

        {!selectedProgram && (
          <Row className="mb-4">
            <Col>
              <Alert variant="info" className="text-center">
                <i className="bi bi-info-circle me-2"></i>
                Please select Group → Program to manage tickets.
              </Alert>
            </Col>
          </Row>
        )}

        {selectedProgram && (
          <Row className="g-3">
            {/* Schedule Selection */}
            <Col xs={12} lg={4}>
              <Card>
                <Card.Header>
                  <h5><i className="bi bi-calendar2-week me-2"></i>Schedules</h5>
                </Card.Header>
                <Card.Body className="schedule-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {schedules.length > 0 ? schedules.map((schedule) => (
                    <Card 
                      key={schedule._id} 
                      className={`mb-2 schedule-card ${selectedSchedule?._id === schedule._id ? 'selected' : ''}`}
                      onClick={() => handleScheduleSelect(schedule)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="p-3">
                        <h6 className="mb-1">{schedule.programId?.title}</h6>
                        <small className="text-muted d-block">
                          <i className="bi bi-calendar me-1"></i>
                          {new Date(schedule.startDate).toLocaleDateString()}
                        </small>
                        <small className="text-muted d-block">
                          <i className="bi bi-clock me-1"></i>
                          {schedule.timing}
                        </small>
                        <small className="text-muted d-block">
                          <i className="bi bi-geo-alt me-1"></i>
                          {schedule.address}
                        </small>
                      </Card.Body>
                    </Card>
                  )) : (
                    <div className="text-center text-muted">
                      <i className="bi bi-calendar-x display-4"></i>
                      <p className="mt-2">No schedules available</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Ticket Options */}
            <Col xs={12} lg={8}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5><i className="bi bi-ticket me-2"></i>Ticket Options</h5>
                  {selectedSchedule && (
                    <Button 
                      variant={ticketOptions.some(t => t.hasBookings) ? "warning" : "primary"}
                      onClick={() => {
                        setNewTicketOptions(ticketOptions.length > 0 ? 
                          ticketOptions.map(t => ({ ticketName: t.ticketName, price: t.price, totalSeats: t.totalSeats || 0 })) :
                          [{ ticketName: "", price: 0, totalSeats: 0 }]
                        );
                        setShowTicketModal(true);
                      }}
                      disabled={((groupCredits?.remainingCredits.ticketCredits ?? 0) <= 0 && ticketOptions.length === 0) || ticketOptions.some(t => t.hasBookings)}
                      title={ticketOptions.some(t => t.hasBookings) ? "Some tickets have bookings - editing restricted" : "Manage ticket options"}
                    >
                      {ticketOptions.some(t => t.hasBookings) ? (
                        <><i className="bi bi-lock me-2"></i>Locked</>
                      ) : (
                        <><i className="bi bi-plus me-2"></i>Manage Tickets</>
                      )}
                    </Button>
                  )}
                </Card.Header>
                <Card.Body>
                  {selectedSchedule ? (
                    ticketOptions.length > 0 ? (
                      <Table striped hover>
                        <thead>
                          <tr>
                            <th>Ticket Name</th>
                            <th>Price</th>
                            <th>Total Seats</th>
                            <th>Available</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ticketOptions.map((option) => (
                            <tr key={option._id} className={option.hasBookings ? 'table-warning' : ''}>
                              <td>
                                {option.ticketName}
                                {option.hasBookings && (
                                  <small className="d-block text-warning">
                                    <i className="bi bi-lock-fill me-1"></i>
                                    Has bookings
                                  </small>
                                )}
                              </td>
                              <td>₹{option.price}</td>
                              <td>{option.totalSeats || 0}</td>
                              <td>{option.availableSeats || 0}</td>
                              <td>
                                <Badge bg={option.isActive ? 'success' : 'secondary'}>
                                  {option.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                                {option.hasBookings && (
                                  <Badge bg="warning" className="ms-1">
                                    <i className="bi bi-lock-fill"></i>
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">
                        <i className="bi bi-info-circle me-2"></i>
                        No ticket options configured for this schedule.
                      </Alert>
                    )
                  ) : (
                    <div className="text-center text-muted py-4">
                      <i className="bi bi-arrow-left display-4"></i>
                      <p className="mt-2">Select a schedule to manage ticket options</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Manage Ticket Options Modal */}
        <Modal show={showTicketModal} onHide={() => setShowTicketModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Manage Ticket Options</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {newTicketOptions.map((option, index) => (
              <Row key={index} className="mb-3 align-items-end">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Ticket Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., VIP, General"
                      value={option.ticketName}
                      onChange={(e) => updateTicketOption(index, 'ticketName', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Price (₹) <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={option.price}
                      onChange={(e) => updateTicketOption(index, 'price', parseFloat(e.target.value) || 0)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Total Seats <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={option.totalSeats}
                      onChange={(e) => updateTicketOption(index, 'totalSeats', parseInt(e.target.value) || 0)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Button
                    variant="outline-danger"
                    onClick={() => removeTicketOption(index)}
                    disabled={newTicketOptions.length === 1}
                  >
                    <i className="bi bi-trash"></i>
                  </Button>
                </Col>
              </Row>
            ))}
            <Button variant="outline-secondary" onClick={addTicketOption} className="mb-3">
              <i className="bi bi-plus me-2"></i>Add Another Option
            </Button>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTicketModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveTicketOptions}
              disabled={ticketOptions.some(t => t.hasBookings)}
              title={ticketOptions.some(t => t.hasBookings) ? "Cannot save - some options have bookings" : "Save ticket options"}
            >
              {ticketOptions.some(t => t.hasBookings) ? "Locked" : "Save Ticket Options"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </PermissionWrapper>
  );
};

export default ManageTicketPage;